import { singleline } from "@core/foundation/string";
import type { ZodType } from "@core/zod";
import { z } from "@core/zod";

const DEFAULT_PAGINATION_LIMIT = 10;

const MAX_PAGINATION_LIMIT = 100;

const PaginationOrderSchema = z.enum(["asc", "desc"]).default("asc");

export const CursorPaginationSchema = z.object(
  {
    cursor: z.string().optional(),
    limit: z
      .int()
      .positive()
      .lte(MAX_PAGINATION_LIMIT)
      .default(DEFAULT_PAGINATION_LIMIT),
    order: PaginationOrderSchema,
  },
  {
    cursor: singleline`
      The nextCursor a previous page answered, to read the page after it; omit
      it to read the first page
    `,
    limit: "The most items the page holds, from 1 to 100; 10 when omitted",
    order:
      "The direction the items are listed in, asc or desc; asc when omitted",
  },
);

export type CursorPagination = z.input<typeof CursorPaginationSchema>;

export const CursorPaginationQuerySchema = z.object(
  {
    cursor: z.string().optional(),
    limit: z.coerce
      .number()
      .int()
      .positive()
      .lte(MAX_PAGINATION_LIMIT)
      .default(DEFAULT_PAGINATION_LIMIT),
    order: PaginationOrderSchema,
  },
  CursorPaginationSchema.descriptions,
);

export const OffsetPaginationSchema = z.object(
  {
    limit: z
      .int()
      .positive()
      .lte(MAX_PAGINATION_LIMIT)
      .default(DEFAULT_PAGINATION_LIMIT),
    offset: z.int().nonnegative().default(0),
    order: PaginationOrderSchema,
  },
  {
    limit: CursorPaginationSchema.descriptions.limit,
    offset: "The number of items to skip before the page; 0 when omitted",
    order: CursorPaginationSchema.descriptions.order,
  },
);

export type OffsetPagination = z.input<typeof OffsetPaginationSchema>;

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function cursorPaginatedResponse<T extends ZodType>(item: T) {
  return z.object(
    { items: z.array(item), nextCursor: z.string().optional() },
    {
      items: "The items of the page",
      nextCursor:
        "The cursor the next page starts from, absent on the last page",
    },
  );
}

export interface CursorPaginatedResponse<Item> {
  readonly items: readonly Item[];
  readonly nextCursor?: string;
}

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function offsetPaginatedResponse<T extends ZodType>(item: T) {
  return z.object(
    { hasMore: z.boolean(), items: z.array(item) },
    {
      hasMore: "Whether more items follow the page",
      items: "The items of the page",
    },
  );
}

export interface OffsetPaginatedResponse<Item> {
  readonly hasMore: boolean;
  readonly items: readonly Item[];
}
